import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "reactstrap";
import {
  COMPANY_INFO_UPDATED_EVENT,
  getCachedCompanyInfo,
  getCompanyDisplayName,
  setCachedCompanyInfo,
} from "@dhn/shared/company-info";
import CompanyLogo from "@dhn/components/CompanyLogo";
import useLoadingClosed from "./use-loading-closed";
import useLoadingOwner from "./use-loading-owner";

// Texto que se muestra cuando no se indica una descripción.
const DEFAULT_LOADING_TEXT = "Cargando...";

// `description` es el texto principal; `isDescription` solo controla la línea
// secundaria, el título siempre se muestra.
const LoadingDhn = ({ isOpen, description, isDescription = true, onClosed }) => {
  const loadingText = description || DEFAULT_LOADING_TEXT;
  const [companyInfo, setCompanyInfo] = useState(() => getCachedCompanyInfo());
  const [isCompanyInfoLoading, setIsCompanyInfoLoading] = useState(false);
  const companyName = getCompanyDisplayName(companyInfo, "Logo");
  const shouldRender = useLoadingClosed(isOpen, onClosed);
  const isVisible = useLoadingOwner(shouldRender);

  useEffect(() => {
    const handleCompanyInfoUpdated = (event) => {
      setCompanyInfo(event.detail || getCachedCompanyInfo());
      setIsCompanyInfoLoading(false);
    };

    window.addEventListener(COMPANY_INFO_UPDATED_EVENT, handleCompanyInfoUpdated);
    return () => {
      window.removeEventListener(COMPANY_INFO_UPDATED_EVENT, handleCompanyInfoUpdated);
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return undefined;

    let isMounted = true;
    const apiBaseUrl = (process.env.REACT_APP_APOLLO_CLIENT_URI || "").replace(/\/$/, "");

    if (!apiBaseUrl) {
      setIsCompanyInfoLoading(false);
      return undefined;
    }

    // La carga entre módulos se abre con frecuencia: se conserva el logo en
    // caché mientras se refresca para que no parpadee en cada navegación.
    const cachedCompanyInfo = getCachedCompanyInfo();
    setCompanyInfo(cachedCompanyInfo);
    setIsCompanyInfoLoading(!cachedCompanyInfo);

    fetch(`${apiBaseUrl}/public/legal-entity?_=${Date.now()}`, { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!isMounted) return;
        if (data) {
          setCachedCompanyInfo(data);
          setCompanyInfo(data);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (isMounted) setIsCompanyInfoLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <Modal
      size="sm"
      isOpen
      centered
      backdrop="static"
      keyboard={false}
      contentClassName="loading-modal-transparent"
    >
      <ModalBody className="loading-modal-body">
        <div
          className="loading-card"
          role="status"
          aria-live="polite"
          aria-label={loadingText}
        >
          <div className="loading-ring" aria-hidden="true">
            <div className="loading-orbit-dot dot-1" />
            <div className="loading-orbit-dot dot-2" />
            <div className="loading-orbit-dot dot-3" />
            <div className="loading-ring-circle ring-1" />
            <div className="loading-ring-circle ring-2" />
            <div className="loading-ring-circle ring-3" />
            <CompanyLogo
              companyInfo={companyInfo}
              loading={isCompanyInfoLoading}
              alt={companyName}
              className="loading-logo"
              loaderClassName="loading-logo"
              loaderStyle={{ fontSize: 18, color: "#1f7db7" }}
            />
          </div>
          <div className="loading-copy">
            <h6 className="loading-title">{loadingText}</h6>
            {isDescription && (
              <p className="loading-subtitle">
                Por favor, espera un momento.
              </p>
            )}
          </div>
          <div className="loading-progress" aria-hidden="true">
            <span />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default LoadingDhn;
